import type {
  TRIPOTWWasteQuantityDataType,
  TRIWasteManagementTypeCodeType,
  TRIToxicEquivalencyIdentificationType,
  TRITransferLocationDataType,
} from '../../types/form-r.ts';
import {
  formRNumbersToSection8Value,
  type ReleasesByChemicalAndFormRLine,
  transfersToXmlCongeners,
} from './form-r-utils.ts';
import type { CasOrCategory } from '../../types/db.ts';
import type { Warning } from '../../types/tri.ts';

export type Section8Total =
  | 'NA'
  | {
      TotalQuantity: number;
      ToxicEquivalencyIdentification: TRIToxicEquivalencyIdentificationType | null;
    };

export interface Section8Quantities {
  // From Section 5:
  OnsiteUICDisposalQuantity: Section8Total;
  OnsiteOtherDisposalQuantity: Section8Total;

  // From Sections 6.1 and 6.2:
  OffsiteUICDisposalQuantity: Section8Total;
  OffsiteOtherDisposalQuantity: Section8Total;
  OnsiteEnergyRecoveryQuantity: Section8Total;
  OffsiteEnergyRecoveryQuantity: Section8Total;
  OnsiteRecycledQuantity: Section8Total;

  // Computed by 8.2, 8.4, and 8.6 formulas:
  OffsiteRecycledQuantity: Section8Total;
  OnsiteTreatedQuantity: Section8Total;
  OffsiteTreatedQuantity: Section8Total;
}

export type Section8QuantityKey = keyof Section8Quantities;

type OffsiteSection8QuantityKey = Extract<
  Section8QuantityKey,
  | 'OffsiteUICDisposalQuantity'
  | 'OffsiteOtherDisposalQuantity'
  | 'OffsiteEnergyRecoveryQuantity'
  | 'OffsiteRecycledQuantity'
  | 'OffsiteTreatedQuantity'
>;

export const formRSection8QuantityKeyByOffsiteWasteManagementType = new Map<
  TRIWasteManagementTypeCodeType,
  OffsiteSection8QuantityKey
>([
  ['M64', 'OffsiteUICDisposalQuantity'],
  ['M65', 'OffsiteUICDisposalQuantity'],
  ['M81', 'OffsiteUICDisposalQuantity'],
  ['M10', 'OffsiteOtherDisposalQuantity'],
  ['M41', 'OffsiteOtherDisposalQuantity'],
  ['M62', 'OffsiteOtherDisposalQuantity'],
  ['M66', 'OffsiteOtherDisposalQuantity'],
  ['M67', 'OffsiteOtherDisposalQuantity'],
  ['M73', 'OffsiteOtherDisposalQuantity'],
  ['M79', 'OffsiteOtherDisposalQuantity'],
  ['M82', 'OffsiteOtherDisposalQuantity'],
  ['M90', 'OffsiteOtherDisposalQuantity'],
  ['M94', 'OffsiteOtherDisposalQuantity'],
  ['M99', 'OffsiteOtherDisposalQuantity'],
  ['M56', 'OffsiteEnergyRecoveryQuantity'],
  ['M92', 'OffsiteEnergyRecoveryQuantity'],
  ['M20', 'OffsiteRecycledQuantity'],
  ['M24', 'OffsiteRecycledQuantity'],
  ['M26', 'OffsiteRecycledQuantity'],
  ['M28', 'OffsiteRecycledQuantity'],
  ['M93', 'OffsiteRecycledQuantity'],
  ['M40', 'OffsiteTreatedQuantity'],
  ['M50', 'OffsiteTreatedQuantity'],
  ['M54', 'OffsiteTreatedQuantity'],
  ['M61', 'OffsiteTreatedQuantity'],
  ['M69', 'OffsiteTreatedQuantity'],
  ['M95', 'OffsiteTreatedQuantity'],
]);

export function formRSection8Sums(
  casOrCategory: CasOrCategory,
  releasesByChemical: ReleasesByChemicalAndFormRLine,
  POTWWasteQuantity: TRIPOTWWasteQuantityDataType[],
  TransferLocation: TRITransferLocationDataType[],
  warnings: Warning[],
): Omit<Section8Quantities, 'OnsiteUICDisposalQuantity' | 'OnsiteOtherDisposalQuantity'> {
  const OffsiteUICDisposalQuantity: Section8Total[] = [];
  const OffsiteOtherDisposalQuantity: Section8Total[] = [];
  const OffsiteEnergyRecoveryQuantity: Section8Total[] = [];
  const OffsiteRecycledQuantity: Section8Total[] = [];
  const OffsiteTreatedQuantity: Section8Total[] = [];

  for (const potw of POTWWasteQuantity) {
    if (potw.WasteQuantity.choice === 0 && potw.POTWTransferTypeCode) {
      if (['P33', 'P34'].includes(potw.POTWTransferTypeCode)) {
        OffsiteUICDisposalQuantity.push({
          TotalQuantity: potw.WasteQuantity.value.WasteQuantityMeasure,
          ToxicEquivalencyIdentification: potw.ToxicEquivalencyIdentification,
        });
      } else if (['P30', 'P31', 'P32', 'P35', 'P36'].includes(potw.POTWTransferTypeCode)) {
        OffsiteOtherDisposalQuantity.push({
          TotalQuantity: potw.WasteQuantity.value.WasteQuantityMeasure,
          ToxicEquivalencyIdentification: potw.ToxicEquivalencyIdentification,
        });
      } else if (['P37', 'P38', 'P39'].includes(potw.POTWTransferTypeCode)) {
        OffsiteTreatedQuantity.push({
          TotalQuantity: potw.WasteQuantity.value.WasteQuantityMeasure,
          ToxicEquivalencyIdentification: potw.ToxicEquivalencyIdentification,
        });
      }
    }
  }

  for (const transferList of TransferLocation) {
    if (transferList.POTWIndicator) {
      continue;
    }

    for (const transfer of transferList.TransferQuantity) {
      if (
        transfer.TransferWasteQuantity === null ||
        transfer.TransferWasteQuantity.Quantity.choice !== 0 ||
        transfer.WasteManagementTypeCode === null
      ) {
        continue;
      }

      const quantityKey = formRSection8QuantityKeyByOffsiteWasteManagementType.get(transfer.WasteManagementTypeCode);
      const section8Total = {
        TotalQuantity: transfer.TransferWasteQuantity.Quantity.value.WasteQuantityMeasure,
        ToxicEquivalencyIdentification: transfer.TransferWasteQuantity.ToxicEquivalencyIdentification,
      };

      if (quantityKey === 'OffsiteUICDisposalQuantity') {
        OffsiteUICDisposalQuantity.push(section8Total);
      } else if (quantityKey === 'OffsiteOtherDisposalQuantity') {
        OffsiteOtherDisposalQuantity.push(section8Total);
      } else if (quantityKey === 'OffsiteEnergyRecoveryQuantity') {
        OffsiteEnergyRecoveryQuantity.push(section8Total);
      } else if (quantityKey === 'OffsiteRecycledQuantity') {
        OffsiteRecycledQuantity.push(section8Total);
      } else if (quantityKey === 'OffsiteTreatedQuantity') {
        OffsiteTreatedQuantity.push(section8Total);
      }
    }
  }

  const OnsiteEnergyRecoveryQuantity: Section8Total[] = [];
  const eightPointTwoReleases = releasesByChemical.get(`${casOrCategory}-8.2`);
  if (eightPointTwoReleases) {
    OnsiteEnergyRecoveryQuantity.push({
      TotalQuantity: eightPointTwoReleases.amountReleased,
      ToxicEquivalencyIdentification: transfersToXmlCongeners(
        casOrCategory,
        '8.2',
        eightPointTwoReleases.transfers,
        warnings,
      ),
    });
  }

  const OnsiteRecycledQuantity: Section8Total[] = [];
  const eightPointFourReleases = releasesByChemical.get(`${casOrCategory}-8.4`);
  if (eightPointFourReleases) {
    OnsiteRecycledQuantity.push({
      TotalQuantity: eightPointFourReleases.amountReleased,
      ToxicEquivalencyIdentification: transfersToXmlCongeners(
        casOrCategory,
        '8.4',
        eightPointFourReleases.transfers,
        warnings,
      ),
    });
  }

  const OnsiteTreatedQuantity: Section8Total[] = [];
  const eightPointSixReleases = releasesByChemical.get(`${casOrCategory}-8.6`);
  if (eightPointSixReleases) {
    OnsiteTreatedQuantity.push({
      TotalQuantity: eightPointSixReleases.amountReleased,
      ToxicEquivalencyIdentification: transfersToXmlCongeners(
        casOrCategory,
        '8.6',
        eightPointSixReleases.transfers,
        warnings,
      ),
    });
  }

  return {
    OffsiteUICDisposalQuantity: formRNumbersToSection8Value(OffsiteUICDisposalQuantity),
    OffsiteOtherDisposalQuantity: formRNumbersToSection8Value(OffsiteOtherDisposalQuantity),
    OnsiteEnergyRecoveryQuantity: formRNumbersToSection8Value(OnsiteEnergyRecoveryQuantity),
    OffsiteEnergyRecoveryQuantity: formRNumbersToSection8Value(OffsiteEnergyRecoveryQuantity),
    OnsiteRecycledQuantity: formRNumbersToSection8Value(OnsiteRecycledQuantity),
    OffsiteRecycledQuantity: formRNumbersToSection8Value(OffsiteRecycledQuantity),
    OnsiteTreatedQuantity: formRNumbersToSection8Value(OnsiteTreatedQuantity),
    OffsiteTreatedQuantity: formRNumbersToSection8Value(OffsiteTreatedQuantity),
  };
}

export function hasNonZeroSection8Quantity(quantities: Section8Quantities): boolean {
  for (const quantity of Object.values(quantities) as Section8Total[]) {
    if (quantity !== 'NA' && quantity.TotalQuantity > 0) {
      return true;
    }
  }

  return false;
}
